import { getNavigatorLanguage } from './getNavigatorLanguage';
import { getTimezoneAbbreviation } from './getTimezoneAbbreviation';

/**
 * Blank/absent zone means "fall back to the viewer's" — Intl throws on '', so
 * callers reading a nullable `venue.timezone` would otherwise have to guard.
 */
const resolveTimeZone = (timeZone?: string | null): string | undefined =>
  timeZone && timeZone.trim() ? timeZone : undefined;

export function formatDate({
  date,
  timeZone,
  locale,
}: {
  date: string | Date;
  timeZone?: string;
  locale?: string;
}): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat(getNavigatorLanguage(locale), {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone,
  }).format(d);
}

export function formatTime({
  date,
  timeZone,
  locale,
  options = {},
}: {
  date: string | Date;
  timeZone?: string;
  locale?: string;
  options?: Intl.DateTimeFormatOptions;
}): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat(getNavigatorLanguage(locale), {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone,
    ...options,
  }).format(d);
}

export function formatWeekday({
  date,
  timeZone,
  locale,
}: {
  date: string | Date;
  timeZone?: string;
  locale?: string;
}): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat(getNavigatorLanguage(locale), {
    weekday: 'long',
    timeZone,
  }).format(d);
}

/** "Saturday Aug. 8" — matches the date-fns `eeee MMM. d` format this replaced. */
export function formatWeekdayMonthDay({
  date,
  timeZone,
  locale,
}: {
  date: string | Date;
  timeZone?: string;
  locale?: string;
}): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const parts = new Intl.DateTimeFormat(getNavigatorLanguage(locale), {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    timeZone,
  }).formatToParts(d);
  const partValue = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';

  return `${partValue('weekday')} ${partValue('month')}. ${partValue('day')}`;
}

/**
 * Returns an ISO date string (yyyy-MM-dd) for a given date in the specified timezone.
 * Uses en-CA locale which formats as yyyy-MM-dd, safe for use as a map key or sort key.
 * A blank/absent zone buckets by the viewer's zone.
 */
export function formatISODateInTimezone(date: string | Date, timeZone?: string | null): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-CA', { timeZone: resolveTimeZone(timeZone) }).format(d);
}

/**
 * Hour of day (0-23) at the given timezone. `hourCycle: 'h23'` rather than
 * `hour12: false` — the latter reports midnight as 24 on some ICU builds, which
 * silently breaks any `hour < 24` bound.
 */
export function getHourInTimezone(date: string | Date, timeZone?: string | null): number {
  const d = typeof date === 'string' ? new Date(date) : date;
  return Number(
    new Intl.DateTimeFormat('en-US', {
      timeZone: resolveTimeZone(timeZone),
      hour: 'numeric',
      hourCycle: 'h23',
    }).format(d),
  );
}

export type FormattedVenueEventDateTime = {
  date: string;
  day: string;
  /** Joined range, e.g. "1:00 – 2:00 PM". Intl drops the first meridiem when both sides share one. */
  time: string;
  /** Always carries its own meridiem — use these when rendering the two ends separately. */
  startTime: string;
  endTime: string;
  timezoneLabel: string;
  timezone: string;
};

/**
 * The one-call shape marketplace cards render: date, weekday, start–end time range
 * and a zone label, all in the venue's local time rather than the viewer's. A
 * marketplace listing is tied to a physical court, so "7 PM" has to mean 7 PM there.
 * Falls back to the viewer's zone for custom-court events with no venue timezone.
 */
export function formatVenueEventDateTime(
  start?: string | null,
  end?: string | null,
  timezone?: string | null,
): FormattedVenueEventDateTime | null {
  if (!start) return null;
  const tz = resolveTimeZone(timezone) ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const startDate = new Date(start);
  const endDate = end ? new Date(end) : null;

  const timeFmt = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return {
    date: new Intl.DateTimeFormat('en-US', { timeZone: tz, month: 'short', day: 'numeric' }).format(
      startDate,
    ),
    day: new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short' }).format(startDate),
    time: endDate ? timeFmt.formatRange(startDate, endDate) : timeFmt.format(startDate),
    startTime: timeFmt.format(startDate),
    endTime: endDate ? timeFmt.format(endDate) : '',
    timezoneLabel: getTimezoneAbbreviation(tz, startDate),
    timezone: tz,
  };
}
